import { Container } from "pixi.js";
import type { IStudioArea } from "@/src/screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Areas.interface";
import type { IStudioElement } from "@/src/screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";
import type { IOverlayElementKeyframes } from "@/src/screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";
import { applyOverlayAnimation, type OverlayFrameUpdater } from "@/src/entities/LivePreview/Strategy/Overlays/OverlayElementAnimator";

const FALLBACK_EPOCH_MS = Date.now();
type CyberMotionElement = IStudioElement & {
  templateMotion?: { family: string; epochMs?: number; animation: IOverlayElementKeyframes };
};

/** One apply-time clock, preserved through text edits and source assignment. */
export function initializeCyberTextMotion(element: IStudioElement, epochMs: number): IStudioElement {
  if (!cyberTextMotion(element)) return element;
  return { ...element, templateMotion: { ...(element as CyberMotionElement).templateMotion!, epochMs } } as CyberMotionElement;
}

export function cyberTextEpoch(element: IStudioElement): number {
  return (element as CyberMotionElement).templateMotion?.epochMs ?? FALLBACK_EPOCH_MS;
}

/** Template-owned metadata: no behavior changes for other editable texts. */
export function cyberTextMotion(element: IStudioElement): IOverlayElementKeyframes | undefined {
  const motion = (element as CyberMotionElement).templateMotion;
  return motion?.family === "sources-cyber" && motion.animation?.kind === "keyframes"
    ? motion.animation : undefined;
}

export function mountCyberTextMotion(element: IStudioElement, container: Container): OverlayFrameUpdater | undefined {
  const animation = cyberTextMotion(element);
  if (!animation) return;
  const fx = new Container();
  fx.addChild(...container.removeChildren());
  container.addChild(fx);
  const handle = { container: fx, animation, w: element.bounds.width * 19.2, h: element.bounds.height * 10.8 };
  const epoch = cyberTextEpoch(element);
  applyOverlayAnimation(handle, Math.max(0, Date.now() - epoch));
  return (elapsed, clockId) => applyOverlayAnimation(handle, Math.max(0, (clockId == null ? Date.now() : clockId + elapsed) - epoch));
}

/** The GAME title identifies an empty slot; an assigned source owns that area. */
export function visibleCyberSourceLabels(studio: { areas: IStudioArea[]; elements?: IStudioElement[] }) {
  const elements = studio.elements ?? [];
  const assigned = studio.areas.some(area =>
    area.overlay?.id === "sources-cyber-area-game-chrome" && !!area.media.resource);
  return assigned ? elements.filter(element => element.id !== "el-sources-cyber-game-label") : elements;
}

/** Pixi's media mask adapter, restricted to the two slots of T-17. */
export function cyberSourcePolygon(
  area: IStudioArea,
  box: { x: number; y: number; width: number; height: number },
): number[] | null {
  if (!/^sources-cyber-area-(game|cam)-chrome$/.test(area.overlay?.id ?? "")) return null;
  const match = /^polygon\((.*)\)$/.exec(area.media.clipPath ?? "");
  if (!match || box.width <= 0 || box.height <= 0) return null;
  const points: number[] = [];
  for (const pair of match[1].split(",")) {
    const point = /^\s*(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%\s*$/.exec(pair);
    if (!point) return null;
    const x = Number(point[1]), y = Number(point[2]);
    if (x > 100 || y > 100) return null;
    points.push(box.x + x / 100 * box.width, box.y + y / 100 * box.height);
  }
  return points.length >= 6 ? points : null;
}
